import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
    LoadingContext,
    M_StateActions,
    MTable,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Role/Profile CRUD page in a tab.
 *
 * Important:
 * - this keeps the original route value used by the existing implementation;
 * - do not change unless the router path for this CRUD page is also changed.
 */
const ROLE_CRUD_ROUTE = "RoleConfig";

//endregion

//region Helpers

/**
 * Builds a readable label value for a Role/Profile row.
 *
 * Purpose:
 * - keeps tab labels safe when Name is missing;
 * - supports alternative fields without breaking the current payload.
 *
 * @param {object|null|undefined} row - Selected role/profile row.
 * @returns {string} Role/Profile display name.
 */
const getRoleDisplayName = (row) =>
    row?.Name ?? row?.name ?? "";

//endregion

//region Component

/**
 * Renders the Roles configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in tabs mode;
 * - keep the page visually aligned with other Config tabs through config-page.css;
 * - render the Profiles MTable;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - persist the selected role/profile row before opening View/Edit tabs.
 *
 * @param {object} props - Component props.
 * @param {Array<object|string>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Roles configuration tab.
 */
export default function RoleConfig({
    roles,
    onCreateTab,
    toastMessages,
}) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this tab.
     *
     * Important:
     * - Print is not part of the desired Roles workflow;
     * - Copy is disabled to keep profile/role creation explicit.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy"],
        []
    );

    /**
     * Opens the Role/Profile CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected role/profile row as rolesLine when required;
     * - opens the configured CRUD route with a localized contextual tab label.
     *
     * Important:
     * - the state key "rolesLine" is preserved for compatibility with the
     *   existing RoleConfig CRUD screen.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected role/profile row.
     * @returns {void}
     */
    const openRoleCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "rolesLine",
                        value: row,
                    })
                );
            }

            const roleName = getRoleDisplayName(row);

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewRole")
                    : operation === "View"
                        ? `${t("LabelViewRole")} "${roleName}"`
                        : `${t("LabelEditRole")} "${roleName}"`;

            onCreateTab(ROLE_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Roles workflow.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openRoleCrud("View", row)],
                ["Edit", (row) => openRoleCrud("Edit", row)],
                ["Create", () => openRoleCrud("Create", null)],
            ]),
        [openRoleCrud]
    );

    return (
        <div
            className="config-page config-page--tabs"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header config-page__header--tabs">
                <div className="config-page__title config-page__title--tabs">
                    {t("Roles")}
                </div>

                <div className="config-page__description config-page__description--tabs">
                    {t("InfoPanelRoles")}
                </div>
            </div>

            <div className="config-page__table config-page__table--tabs">
                <MTable
                    entityName="Profiles"
                    roleName="Profiles"
                    rolePermissions={roles}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                    tabsRailWidth={250}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

RoleConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion